import app from '../../forum/app';
import EditPostComposer from '../components/EditPostComposer';
import Button from '../../common/components/Button';
import Separator from '../../common/components/Separator';
import ItemList from '../../common/utils/ItemList';
import extractText from '../../common/utils/extractText';
import AlertUtil from '../../forum/dianLiang/alertUtil';
import LogInModal from '../../forum/components/LogInModal';

/**
 * The `PostControls` utility constructs a list of buttons for a post which
 * perform actions on it.
 */
export default {
  /**
   * Get a list of controls for a post.
   *
   * @param {import('../../common/models/Post').default} post
   * @param {import('../../common/Component').default<any, any>}  context The parent component under which the controls menu will be displayed.
   *
   * @return {ItemList<import('mithril').Children>}')}
   */
  controls(post, context) {
    const items = new ItemList();

    ['user', 'moderation', 'destructive'].forEach((section) => {
      const controls = this[section + 'Controls'](post, context).toArray();
      if (controls.length) {
        controls.forEach((item) => items.add(item.itemName, item));
        items.add(section + 'Separator', <Separator />);
      }
    });

    return items;
  },

  /**
   * Get controls for a post pertaining to the current user (e.g. report).
   *
   * @param {import('../../common/models/Post').default} post
   * @param {import('../../common/Component').default<any, any>}  context The parent component under which the controls menu will be displayed.
   *
   * @return {ItemList<import('mithril').Children>}')}
   * @protected
   */
  userControls(post, context) {
    return new ItemList();
  },

  /**
   * Get controls for a post pertaining to moderation (e.g. edit).
   *
   * @param {import('../../common/models/Post').default} post
   * @param {import('../../common/Component').default<any, any>}  context The parent component under which the controls menu will be displayed.
   *
   * @return {ItemList<import('mithril').Children>}')}
   * @protected
   */
  moderationControls(post, context) {
    const items = new ItemList();

    if (post.contentType() === 'comment' && post.canEdit() && post.user() == app.session.user) {
      // if (!post.isHidden()) {
      items.add(
        'edit',
        <Button
          icon="fas fa-pencil-alt"
          onclick={() => {
            this.editAction.bind(post)();
            if (!app.session.user.isAdmin()) {
              let time = app.forum.attribute('postEditTime');
              if (time != '-1' && time != 'reply') {
                AlertUtil.onWarn(app, app.translator.trans('dl-response.forum.edit_content',{time: time}));
              }
            }
          }}
        >
          {app.translator.trans('core.forum.post_controls.edit_button')}
        </Button>
      );
      // }
    }

    return items;
  },

  /**
   * Get controls for a post that are destructive (e.g. delete).
   *
   * @param {import('../../common/models/Post').default} post
   * @param {import('../../common/Component').default<any, any>}  context The parent component under which the controls menu will be displayed.
   *
   * @return {ItemList<import('mithril').Children>}')}
   * @protected
   */
  destructiveControls(post, context) {
    const items = new ItemList();

    if (post.contentType() === 'comment' && post.canHide()) {
      if (!post.isHidden()) {
        items.add(
          'hide',
          <Button icon="far fa-trash-alt" onclick={this.hideAction.bind(post)}>
            {app.translator.trans('dl-index.forum.permissions.hide_label')}
          </Button>
        );
      } else {
        items.add(
          'restore',
          <Button icon="fas fa-restore" onclick={this.restoreAction.bind(post)}>
            {app.translator.trans('dl-index.forum.permissions.restore_label')}
          </Button>
        );
      }
    }
    if (post.canDelete()) {
      items.add(
        'delete',
        <Button icon="fas fa-times" onclick={this.deleteAction.bind(post, context)}>
          {app.translator.trans('dl-index.forum.permissions.delete_label')}
        </Button>
      );
    }

    return items;
  },

  /**
   * Open the composer to edit a post.
   *
   * @return {Promise<void>}
   */
  editAction() {
    return new Promise((resolve) => {
      app.composer.load(EditPostComposer, { post: this });
      app.composer.show();

      return resolve();
    });
  },

  /**
   * Hide a post.
   *
   * @return {Promise<void>}
   */
  hideAction() {
    if (!confirm(extractText(app.translator.trans('dl-index.forum.permissions.hide_confirmation')))) return;
    this.pushData({ attributes: { hiddenAt: new Date() }, relationships: { hiddenUser: app.session.user } });

    return this.save({ isHidden: true }).then(AlertUtil.onSuccess(app, app.translator.trans('dl-index.forum.permissions.success_hide_label')));
  },

  /**
   * Restore a post.
   *
   * @return {Promise<void>}
   */
  restoreAction() {
    this.pushData({ attributes: { hiddenAt: null }, relationships: { hiddenUser: null } });

    return this.save({ isHidden: false }).then(AlertUtil.onSuccess(app, app.translator.trans('dl-index.forum.permissions.success_restore_label')));
  },

  /**
   * Delete a post.
   *
   * @return {Promise<void>}
   */
  deleteAction(context) {
    if (!confirm(extractText(app.translator.trans('core.forum.post_controls.delete_confirmation')))) return;
    if (context) context.loading = true;

    return this.delete()
      .then(() => {
        const discussion = this.discussion();

        discussion.removePost(this.id());

        // If this was the last post in the discussion, then we will assume that
        // the whole discussion was deleted too.
        if (!discussion.postIds().length || !discussion.firstPost()) {
          app.discussions.removeDiscussion(discussion);

          if (app.viewingDiscussion(discussion)) {
            app.history.back();
          }
        }
        // 从父评论的子评论列表中移除当前评论
        const parentId = this.replyTo();
        if (parentId) {
          const parentPost = app.cache.trees[parentId];
          if (parentPost) {
            const replies = parentPost.filter((post) => post.id() !== this.id());
            app.cache.trees[parentId] = replies;
          }
        }
      })
      .catch(() => {})
      .then(() => {
        if (context) context.loading = false;
        AlertUtil.onSuccess(app, app.translator.trans('dl-index.forum.permissions.success_delete_label'));
        // app.history.back();
        m.redraw();
      });
  },
};
